import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, Building2, Mail, Phone, Globe } from 'lucide-react';

const Impressum = () => {
  const { language } = useLanguage();

  const content = {
    sv: {
      title: "Impressum",
      subtitle: "Juridisk information enligt EU-krav",
      provider: "Ansvarig utgivare",
      contact: "Kontaktuppgifter",
      disclaimer: "Ansvarsfriskrivning",
      disclaimerText: "Denna applikation tillhandahålls som ett beräkningsverktyg för skogsekonomiska scenarier. Beräkningarna baseras på statistiska genomsnittsvärden från officiella svenska källor (SLU Riksskogstaxeringen, Skogsstyrelsen) och ska endast användas som vägledning. Faktiska resultat kan variera avsevärt beroende på lokala förhållanden, marknadssituationen och andra faktorer. Vi rekommenderar att alltid konsultera en kvalificerad skogsrådgivare innan ekonomiska beslut fattas.",
      copyright: "Upphovsrätt",
      copyrightText: "© 2026 Skogskalkyl. Alla rättigheter förbehållna. Innehållet på denna webbplats, inklusive men inte begränsat till text, grafik, logotyper och programvara, skyddas av upphovsrätt och andra immateriella rättigheter.",
      disputes: "Tvistlösning",
      disputesText: "EU-kommissionen tillhandahåller en plattform för tvistlösning online (ODR): https://ec.europa.eu/consumers/odr. Vi deltar för närvarande inte i förfaranden inför en konsumenttvistnämnd.",
      back: "Tillbaka till startsidan"
    },
    de: {
      title: "Impressum",
      subtitle: "Rechtliche Informationen gemäß EU-Anforderungen",
      provider: "Anbieter",
      contact: "Kontakt",
      disclaimer: "Haftungsausschluss",
      disclaimerText: "Diese Anwendung wird als Berechnungstool für forstwirtschaftliche Szenarien bereitgestellt. Die Berechnungen basieren auf statistischen Durchschnittswerten aus offiziellen schwedischen Quellen (SLU Reichswaldinventur, Schwedische Forstbehörde) und dienen nur als Orientierungshilfe. Die tatsächlichen Ergebnisse können je nach lokalen Gegebenheiten, Marktsituation und anderen Faktoren erheblich variieren. Wir empfehlen, vor wirtschaftlichen Entscheidungen immer einen qualifizierten Forstberater zu konsultieren.",
      copyright: "Urheberrecht",
      copyrightText: "© 2026 Skogskalkyl. Alle Rechte vorbehalten. Der Inhalt dieser Website, einschließlich aber nicht beschränkt auf Texte, Grafiken, Logos und Software, ist durch Urheberrechte und andere geistige Eigentumsrechte geschützt.",
      disputes: "Streitbeilegung",
      disputesText: "Die EU-Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit: https://ec.europa.eu/consumers/odr. Wir nehmen derzeit nicht an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teil.",
      back: "Zurück zur Startseite"
    },
    en: {
      title: "Legal Notice",
      subtitle: "Legal information according to EU requirements",
      provider: "Provider",
      contact: "Contact",
      disclaimer: "Disclaimer",
      disclaimerText: "This application is provided as a calculation tool for forest economy scenarios. The calculations are based on statistical average values from official Swedish sources (SLU National Forest Inventory, Swedish Forest Agency) and should only be used as guidance. Actual results may vary significantly depending on local conditions, market situation, and other factors. We recommend always consulting a qualified forestry advisor before making economic decisions.",
      copyright: "Copyright",
      copyrightText: "© 2026 Skogskalkyl. All rights reserved. The content on this website, including but not limited to text, graphics, logos, and software, is protected by copyright and other intellectual property rights.",
      disputes: "Dispute Resolution",
      disputesText: "The EU Commission provides a platform for online dispute resolution (ODR): https://ec.europa.eu/consumers/odr. We currently do not participate in dispute resolution proceedings before a consumer arbitration board.",
      back: "Back to homepage"
    },
    se: {
      title: "Juridihkalaš dieđut",
      subtitle: "Juridihkalaš dieđut EU-gáibádusaid mielde",
      provider: "Fállá",
      contact: "Oktavuohta",
      disclaimer: "Vásttolasvuođafriddjavuohta",
      disclaimerText: "Dát aplikašuvdna fállojuvvo rehkenstanreaiddun vuovdeekonomalaš skenáriojaide. Rehkenastimat vuođđuduvvojit statistihkalaš gaskamearreárvvuide virggálaš ruoŧŧelaš gálduin ja galget dušše geavahuvvot bagadallan. Duohta bohtosat sáhttet rievdat mearkkašahtti láhkai báikkálaš diliid, márkanadili ja eará fáktoriid dihte.",
      copyright: "Dahkkivuoigatvuohta",
      copyrightText: "© 2026 Skogskalkyl. Buot vuoigatvuođat vurkejuvvon.",
      disputes: "Riiddočoavdin",
      disputesText: "EU-kommišuvdna fállá vuogádaga riiddočoavdimii neahtas (ODR): https://ec.europa.eu/consumers/odr.",
      back: "Ruoktot álgosiidui"
    }
  };

  const t = content[language] || content.sv;

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="impressum-page">
      <Link 
        to="/" 
        className="inline-flex items-center text-sm text-forest-600 hover:text-forest-700"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        {t.back}
      </Link>

      <div>
        <h1 className="text-3xl font-heading font-bold text-forest-800">{t.title}</h1>
        <p className="text-muted-foreground mt-2">{t.subtitle}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-forest-600" />
            {t.provider}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="font-semibold">Skogskalkyl</p>
            <p className="text-muted-foreground">Skogsekonomisk beräkningstjänst</p>
          </div>
          <Separator />
          <div className="space-y-2">
            <p className="font-medium">{t.contact}</p>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Mail className="h-4 w-4" />
              <span>info@hodema.se</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Globe className="h-4 w-4" />
              <span>www.skogskalkyl.se</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t.disclaimer}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground leading-relaxed">{t.disclaimerText}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t.copyright}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground leading-relaxed">{t.copyrightText}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t.disputes}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground leading-relaxed">{t.disputesText}</p>
          <a 
            href="https://ec.europa.eu/consumers/odr" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-forest-600 hover:underline mt-2 inline-block"
          >
            EU Online-Streitbeilegung →
          </a>
        </CardContent>
      </Card>
    </div>
  );
};

export default Impressum;
